import type { ProviderKind } from '@hangar/shared';
import { cn } from '@/lib/utils';
import { FormField, fieldA11y } from '@/components/FormField';
import { Icon } from '@/components/Icon';
import { ProblemAlert } from '@/components/ProblemAlert';
import { Tag } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { SheetDescription, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { CONNECT_COPY } from './providerAccountModel';
import { AccountSource, ProviderTokenGuide, ProviderTokenVerify } from './ProviderTokenParts';
import { useConnectProviderForm, type ConnectProviderForm as Form } from './useConnectProviderForm';

const KINDS: ProviderKind[] = ['eas', 'bitrise'];
const KIND_LABEL: Record<ProviderKind, string> = { eas: 'Expo EAS', bitrise: 'Bitrise' };

const optionClass = (selected: boolean, unavailable: boolean) =>
  cn(
    'flex h-9 items-center justify-center gap-1.5 rounded-md text-[13px] font-medium transition-colors duration-150 max-sm:h-11',
    selected ? 'bg-card text-foreground shadow-card' : 'text-muted-foreground',
    unavailable ? 'cursor-not-allowed opacity-60' : 'hover:text-foreground',
  );

function ProviderOption({ kind, selected, unavailable, onPick }: { kind: ProviderKind; selected: boolean; unavailable: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-disabled={unavailable || undefined}
      title={unavailable ? 'Bitrise builds are available soon' : undefined}
      data-testid={`provider-account-kind-${kind}`}
      onClick={() => (unavailable ? undefined : onPick())}
      className={optionClass(selected, unavailable)}
    >
      {KIND_LABEL[kind]}
      {unavailable ? <Tag>Available soon</Tag> : null}
    </button>
  );
}

function ProviderPicker({ f }: { f: Form }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label className="text-[13px]">Provider</Label>
      <div role="radiogroup" aria-label="Provider" className="grid grid-cols-2 gap-1 rounded-lg border bg-secondary p-1">
        {KINDS.map((k) => (
          <ProviderOption key={k} kind={k} selected={f.values.kind === k} unavailable={k === 'bitrise' && !f.bitrise} onPick={() => f.setKind(k)} />
        ))}
      </div>
    </div>
  );
}

function TokenStored({ onReplace }: { onReplace: () => void }) {
  return (
    <div data-testid="provider-account-token-stored" className="flex items-start gap-2 rounded-lg border border-succeeded/30 bg-succeeded/10 px-3 py-2 text-[13px] text-foreground">
      <Icon name="key" size={16} className="mt-0.5 shrink-0 text-succeeded" />
      <span className="min-w-0 flex-1">The token is stored; it is attached to the account when you connect it.</span>
      <Button type="button" size="sm" variant="ghost" className="-my-1" onClick={onReplace}>
        Use another token
      </Button>
    </div>
  );
}

function TokenField({ f }: { f: Form }) {
  if (f.reuse) return <TokenStored onReplace={f.forgetToken} />;
  const copy = CONNECT_COPY[f.values.kind];
  return (
    <FormField id="provider-account-token" label={copy.tokenLabel} required hint={copy.tokenHelp} error={f.errorFor('token')}>
      <Input data-testid="provider-account-token" type="password" value={f.values.token} onChange={(e) => f.set('token', e.target.value)} className="font-mono sm:text-[13px]" autoComplete="off" spellCheck={false} {...fieldA11y('provider-account-token', f.errorFor('token'), 'hint')} />
    </FormField>
  );
}

function NameField({ f }: { f: Form }) {
  return (
    <FormField id="provider-account-name" label="Name" required hint="How Hangar shows it; proposed once the token is verified" error={f.errorFor('name')}>
      <Input data-testid="provider-account-name" value={f.values.name} onChange={(e) => f.set('name', e.target.value)} placeholder={CONNECT_COPY[f.values.kind].namePlaceholder} maxLength={120} autoComplete="off" {...fieldA11y('provider-account-name', f.errorFor('name'), 'hint')} />
    </FormField>
  );
}

function SlotsField({ f }: { f: Form }) {
  const error = f.errorFor('concurrencyLimit', true);
  return (
    <FormField id="provider-account-limit" label="Slots" required hint={CONNECT_COPY[f.values.kind].slotsHint} error={error}>
      <Input data-testid="provider-account-limit" type="number" min={1} max={100} value={f.values.concurrencyLimit} onChange={(e) => f.set('concurrencyLimit', e.target.value)} className="font-mono sm:max-w-32 sm:text-[13px]" {...fieldA11y('provider-account-limit', error, 'hint')} />
    </FormField>
  );
}

function ConnectFooter({ busy, onCancel }: { busy: boolean; onCancel: () => void }) {
  return (
    <SheetFooter className="flex-row justify-end border-t">
      <Button type="button" variant="ghost" onClick={onCancel}>
        Cancel
      </Button>
      <Button type="submit" data-testid="provider-account-submit" loading={busy}>
        <Icon name="hub" size={18} />
        Connect account
      </Button>
    </SheetFooter>
  );
}

/**
 * The sheet's content when connecting (platform operators): provider, how to create the token, the token, the account it
 * reaches, what the provider said about it, then the proposed name and slots. Nothing is stored until it verifies.
 */
export function ConnectForm({ initialKind, onDone }: { initialKind?: ProviderKind; onDone: () => void }) {
  const f = useConnectProviderForm(initialKind, onDone);
  const copy = CONNECT_COPY[f.values.kind];
  return (
    <form data-testid="provider-account-form" data-kind={f.values.kind} onSubmit={(e) => void f.onSubmit(e)} noValidate className="flex h-full min-h-0 flex-col" aria-label={copy.title}>
      <SheetHeader>
        <SheetTitle>{copy.title}</SheetTitle>
        <SheetDescription id="provider-account-desc" className="text-xs">
          {copy.intro} It is checked with {copy.provider} first, stored encrypted and never shown again.
        </SheetDescription>
      </SheetHeader>
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-5">
        <ProviderPicker f={f} />
        {f.reuse ? null : <ProviderTokenGuide kind={f.values.kind} />}
        <TokenField f={f} />
        {f.reuse ? null : (
          <>
            <AccountSource kind={f.values.kind} check={f.check} account={f.account} typed={f.values.externalAccount} error={f.errorFor('externalAccount')} onChange={(v) => f.set('externalAccount', v)} />
            <ProviderTokenVerify kind={f.values.kind} check={f.check} />
          </>
        )}
        <NameField f={f} />
        <SlotsField f={f} />
        <ProblemAlert error={f.problem} testId="provider-account-problem" />
      </div>
      <ConnectFooter busy={f.busy} onCancel={onDone} />
    </form>
  );
}
